<div class="user-detail-container" *ngIf="user">
  <!-- Personal Information -->
  <div class="detail-card">
    <div class="account-section-header">
      <h3>Personal Information</h3>
      <div class="account-section-actions" *ngIf="canEditPersonalInfo">
        <ng-container *ngIf="!editingPersonal; else personalEditActions">
          <button
            class="btn btn-secondary btn-small"
            data-testid="btn-edit-personal"
            (click)="startEditPersonal()"
          >Edit</button>
        </ng-container>
        <ng-template #personalEditActions>
          <button
            class="btn btn-secondary btn-small margin-right-10"
            data-testid="btn-cancel-personal"
            [disabled]="isSaving"
            (click)="cancelEditPersonal()"
          >Cancel</button>
          <button
            class="btn btn-primary btn-small"
            data-testid="btn-save-personal"
            [disabled]="isSaving"
            (click)="savePersonal()"
          >Save</button>
        </ng-template>
      </div>
    </div>

    <div class="form-group">
      <div class="form-row two-item">
        <div class="form-field">
          <label for="firstName">First Name</label>
          <input
            type="text"
            id="firstName"
            data-testid="input-user-first-name"
            [value]="editingPersonal ? personalDraft.FIRST_NAME : user.FIRST_NAME"
            [disabled]="!editingPersonal"
            (input)="personalDraft.FIRST_NAME = $any($event.target).value"
          >
        </div>

        <div class="form-field">
          <label for="lastName">Last Name</label>
          <input
            type="text"
            id="lastName"
            data-testid="input-user-last-name"
            [value]="editingPersonal ? personalDraft.LAST_NAME : user.LAST_NAME"
            [disabled]="!editingPersonal"
            (input)="personalDraft.LAST_NAME = $any($event.target).value"
          >
        </div>
      </div>

      <div class="form-row one-item">
        <div class="form-field">
          <label for="email">Email</label>
          <input
            type="email"
            id="email"
            data-testid="input-user-email"
            [value]="editingPersonal ? personalDraft.EMAIL : user.EMAIL"
            [disabled]="!editingPersonal"
            (input)="personalDraft.EMAIL = $any($event.target).value"
          >
        </div>
      </div>
    </div>
  </div>

  <!-- Organization Information -->
  <div class="detail-card margin-top-20">
    <div class="account-section-header">
      <h3>Organization Information</h3>
      <div class="account-section-actions" *ngIf="canEditOrganizationSection">
        <ng-container *ngIf="!editingOrganization; else orgEditActions">
          <button
            class="btn btn-secondary btn-small"
            data-testid="btn-edit-organization"
            (click)="startEditOrganization()"
          >Edit</button>
        </ng-container>
        <ng-template #orgEditActions>
          <button
            class="btn btn-secondary btn-small margin-right-10"
            data-testid="btn-cancel-organization"
            [disabled]="isSaving"
            (click)="cancelEditOrganization()"
          >Cancel</button>
          <button
            class="btn btn-primary btn-small"
            data-testid="btn-save-organization"
            [disabled]="isSaving"
            (click)="saveOrganization()"
          >Save</button>
        </ng-template>
      </div>
    </div>

    <div class="form-group">
      <div class="form-row two-item">

        <!-- Org group: super admin editable dropdown; everyone else readonly -->
        <div class="form-field">
          <label for="organizationGroupId">Organization Group</label>
          <ng-container *ngIf="canEditOrgGroup && editingOrganization; else orgGroupReadonly">
            <select
              id="organizationGroupId"
              data-testid="input-user-organization-group-id"
              [(ngModel)]="orgDraft.ORGANIZATION_GROUP_ID"
              [ngModelOptions]="{ standalone: true }"
              (ngModelChange)="onOrgGroupChange()"
            >
              <option [ngValue]="null" disabled>Select a group</option>
              <option *ngFor="let g of organizationGroups" [ngValue]="g.GROUP_ID">{{ g.GROUP_NAME }}</option>
            </select>
          </ng-container>
          <ng-template #orgGroupReadonly>
            <input
              type="text"
              id="organizationGroupId"
              data-testid="input-user-organization-group-id"
              [value]="displayOrgGroupName"
              disabled
            >
          </ng-template>
        </div>

        <!-- Role: editable dropdown for all editors (options vary by role) -->
        <div class="form-field">
          <label for="role">Role</label>
          <ng-container *ngIf="editingOrganization; else roleReadonly">
            <select
              id="role"
              data-testid="input-user-role"
              [(ngModel)]="orgDraft.ROLE"
              [ngModelOptions]="{ standalone: true }"
            >
              <option *ngFor="let r of availableRoleOptions" [ngValue]="r">{{ r }}</option>
            </select>
          </ng-container>
          <ng-template #roleReadonly>
            <input
              type="text"
              id="role"
              data-testid="input-user-role"
              [value]="user.ROLE"
              disabled
            >
          </ng-template>
        </div>
      </div>

      <div class="form-row one-item">

        <!-- Org: super admin + supervisor editable dropdown; manager readonly -->
        <div class="form-field">
          <label for="organizationId">Organization</label>
          <ng-container *ngIf="canEditOrg && editingOrganization; else orgReadonly">
            <select
              id="organizationId"
              data-testid="input-user-organization-id"
              [(ngModel)]="orgDraft.ORGANIZATION_ID"
              [ngModelOptions]="{ standalone: true }"
              [disabled]="!orgDraft.ORGANIZATION_GROUP_ID || loadingOrgs"
            >
              <option [ngValue]="null" disabled>
                {{ loadingOrgs ? 'Loading...' : (orgDraft.ORGANIZATION_GROUP_ID ? 'Select an organization' : 'Select a group first') }}
              </option>
              <option *ngFor="let org of filteredOrgs" [ngValue]="org.ORGANIZATION_ID">{{ org.ORGANIZATION_NAME }}</option>
            </select>
          </ng-container>
          <ng-template #orgReadonly>
            <input
              type="text"
              id="organizationId"
              data-testid="input-user-organization-id"
              [value]="displayOrgName"
              disabled
            >
          </ng-template>
        </div>
      </div>
    </div>
  </div>

  <!-- Account Information (read-only) -->
  <div class="detail-card margin-top-20">
    <h3 class="margin-bottom-20">Account Information</h3>
    <div class="form-group">
      <div class="form-row one-item">
        <div class="form-field">
          <label for="userId">User ID</label>
          <input type="text" id="userId" data-testid="input-user-user-id" [value]="user.USER_ID" disabled>
        </div>
      </div>

      <div class="form-row one-item">
        <div class="form-field">
          <label for="cognitoId">Access ID</label>
          <input type="text" id="cognitoId" data-testid="input-user-cognito-id" [value]="user.COGNITO_ID" disabled>
        </div>
      </div>

      <div class="form-row two-item">
        <div class="form-field">
          <label for="createdAt">Created At</label>
          <input type="text" id="createdAt" data-testid="input-user-created-at" [value]="formatDate(user.CREATED_AT)" disabled>
        </div>

        <div class="form-field">
          <label for="updatedAt">Last Updated</label>
          <input type="text" id="updatedAt" data-testid="input-user-updated-at" [value]="formatDate(user.UPDATED_AT)" disabled>
        </div>
      </div>
    </div>
  </div>
</div>

<div class="loading-container" *ngIf="!user">
  <div class="loading-message">Loading user information...</div>
</div>
